/**
 * AdBanner Component - Google AdSense Integration
 * 
 * Displays ads ONLY for contractors with ZERO paid features/bundles.
 * Features:
 * - Server-side + client-side eligibility checks
 * - Frequency capping (session + daily)
 * - Layout shift prevention
 * - Clean failure handling
 * - Impression logging
 */

import { useState, useEffect, useRef, useCallback } from "react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Session storage keys for frequency capping
const SESSION_KEY = "fsp_ad_session_impressions";
const DAY_KEY = "fsp_ad_day_impressions";
const DAY_DATE_KEY = "fsp_ad_day_date";

// Excluded pages - ads will NEVER show on these
const EXCLUDED_PAGES = [
  "/billing",
  "/upgrades",
  "/checkout",
  "/pricing",
  "/job/new",
  "/job/edit",
  "/estimate/new",
  "/estimate/edit",
  "/invoice/new",
  "/invoice/edit",
  "/customer/new",
  "/customer/edit",
];

// Check if current page is excluded
const isExcludedPage = () => {
  const path = window.location.pathname.toLowerCase();
  return EXCLUDED_PAGES.some(excluded => path.includes(excluded));
};

// Get session impression count
const getSessionImpressions = () => {
  try {
    return parseInt(sessionStorage.getItem(SESSION_KEY) || "0", 10);
  } catch {
    return 0;
  }
};

// Increment session impressions
const incrementSessionImpressions = () => {
  try {
    const current = getSessionImpressions();
    sessionStorage.setItem(SESSION_KEY, String(current + 1));
    return current + 1;
  } catch {
    return 1;
  }
};

// Get daily impression count
const getDayImpressions = () => {
  try {
    const today = new Date().toISOString().split("T")[0];
    const storedDate = localStorage.getItem(DAY_DATE_KEY);
    
    if (storedDate !== today) {
      // New day, reset counter
      localStorage.setItem(DAY_DATE_KEY, today);
      localStorage.setItem(DAY_KEY, "0");
      return 0;
    }
    
    return parseInt(localStorage.getItem(DAY_KEY) || "0", 10);
  } catch {
    return 0;
  }
};

// Increment daily impressions
const incrementDayImpressions = () => {
  try {
    const today = new Date().toISOString().split("T")[0];
    localStorage.setItem(DAY_DATE_KEY, today);
    const current = getDayImpressions();
    localStorage.setItem(DAY_KEY, String(current + 1));
    return current + 1;
  } catch {
    return 1;
  }
};

/**
 * AdBanner Component
 * 
 * @param {string} placement - One of: dashboard_footer, listview_inline, settings_footer
 * @param {string} className - Additional CSS classes
 */
export default function AdBanner({ placement = "dashboard_footer", className = "" }) {
  const [eligible, setEligible] = useState(false);
  const [adConfig, setAdConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [adLoaded, setAdLoaded] = useState(false);
  const containerRef = useRef(null);
  const adInitialized = useRef(false);

  // Check eligibility on mount
  useEffect(() => {
    checkEligibility();
  }, [placement]);

  const checkEligibility = async () => {
    setLoading(true);
    setError(null);

    // Client-side check: excluded pages
    if (isExcludedPage()) {
      logImpression(placement, false, "excluded_page");
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API}/ads/eligibility`, {
        credentials: "include",
      });

      if (!response.ok) {
        setLoading(false);
        return;
      }

      const data = await response.json();

      if (!data.eligible) {
        logImpression(placement, false, data.reason);
        setLoading(false);
        return;
      }

      // Check frequency caps
      const sessionImpressions = getSessionImpressions();
      const dayImpressions = getDayImpressions();
      const caps = data.frequency_caps || {};
      const maxSession = caps.max_impressions_per_session || 5;
      const maxDay = caps.max_impressions_per_day || 20;

      if (sessionImpressions >= maxSession) {
        logImpression(placement, false, "session_cap_reached", null, sessionImpressions, dayImpressions);
        setLoading(false);
        return;
      }

      if (dayImpressions >= maxDay) {
        logImpression(placement, false, "day_cap_reached", null, sessionImpressions, dayImpressions);
        setLoading(false);
        return;
      }

      // Find ad unit for this placement
      const adUnit = (data.ad_units || []).find(u => u.placement_key === placement);
      if (!adUnit) {
        logImpression(placement, false, "no_ad_unit_configured");
        setLoading(false);
        return;
      }

      // User is eligible!
      setAdConfig({
        publisherId: data.publisher_id,
        adSlotId: adUnit.ad_slot_id,
        format: adUnit.format || "auto",
        mode: data.ads_mode,
        caps: data.frequency_caps,
      });
      setEligible(true);

    } catch (err) {
      console.error("Ad eligibility check failed:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Load AdSense script and initialize ad
  useEffect(() => {
    if (!eligible || !adConfig || adInitialized.current) return;

    const loadAdSense = async () => {
      try {
        // Load AdSense script if not already loaded
        if (!window.adsbygoogle) {
          const script = document.createElement("script");
          script.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${adConfig.publisherId}`;
          script.async = true;
          script.crossOrigin = "anonymous";
          
          // For test mode, use test client
          if (adConfig.mode === "test") {
            script.setAttribute("data-ad-test", "on");
          }

          await new Promise((resolve, reject) => {
            script.onload = resolve;
            script.onerror = reject;
            document.head.appendChild(script);
          });
        }

        // Initialize the ad
        adInitialized.current = true;
        window.adsbygoogle = window.adsbygoogle || [];
        window.adsbygoogle.push({});

        // Track impression
        incrementSessionImpressions();
        incrementDayImpressions();
        logImpression(placement, true);
        setAdLoaded(true);

      } catch (err) {
        console.error("AdSense load failed:", err);
        logImpression(placement, false, "script_load_failed", err.message);
        setError("ad_load_failed");
      }
    };

    loadAdSense();
  }, [eligible, adConfig, placement]);

  // Log impression to backend
  const logImpression = useCallback(async (
    placementKey,
    rendered,
    suppressionReason = null,
    errorCode = null,
    sessionImpressions = null,
    dayImpressions = null
  ) => {
    try {
      await fetch(`${API}/ads/impression`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          placement_key: placementKey,
          rendered,
          suppression_reason: suppressionReason,
          error_code: errorCode,
          page_url: window.location.pathname,
          session_impressions: sessionImpressions ?? getSessionImpressions(),
          day_impressions: dayImpressions ?? getDayImpressions(),
        }),
      });
    } catch (err) {
      console.error("Failed to log ad impression:", err);
    }
  }, []);

  // Don't render anything if not eligible or still loading
  if (loading || !eligible || !adConfig) {
    return null;
  }

  // Error state - hide cleanly
  if (error) {
    return null;
  }

  // Render the ad container
  return (
    <div
      ref={containerRef}
      className={`fsp-ad-container ${className}`}
      style={{
        minHeight: adLoaded ? "auto" : "90px",
        width: "100%",
        overflow: "hidden",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        backgroundColor: adLoaded ? "transparent" : "#f8f8f8",
        borderRadius: "4px",
        margin: "16px 0",
      }}
      data-testid={`ad-banner-${placement}`}
    >
      {!adLoaded && (
        <span
          style={{
            fontSize: "10px",
            color: "#999",
            textTransform: "uppercase",
            letterSpacing: "1px",
          }}
        >
          Advertisement
        </span>
      )}
      <ins
        className="adsbygoogle"
        style={{
          display: "block",
          width: "100%",
          minHeight: "90px",
        }}
        data-ad-client={adConfig.publisherId}
        data-ad-slot={adConfig.adSlotId}
        data-ad-format={adConfig.format}
        data-full-width-responsive="true"
      />
    </div>
  );
}

/**
 * Hook to check if ads should be shown
 * Use this to conditionally render AdBanner only when appropriate
 */
export function useAdEligibility() {
  const [eligibility, setEligibility] = useState({
    loading: true,
    eligible: false,
    reason: null,
  });

  useEffect(() => {
    const check = async () => {
      // Client-side check: excluded pages
      if (isExcludedPage()) {
        setEligibility({ loading: false, eligible: false, reason: "excluded_page" });
        return;
      }

      try {
        const response = await fetch(`${API}/ads/eligibility`, {
          credentials: "include",
        });

        if (!response.ok) {
          setEligibility({ loading: false, eligible: false, reason: "fetch_failed" });
          return;
        }

        const data = await response.json();
        setEligibility({
          loading: false,
          eligible: data.eligible,
          reason: data.reason || null,
        });
      } catch (err) {
        setEligibility({ loading: false, eligible: false, reason: "error" });
      }
    };

    check();
  }, []);

  return eligibility;
}
